import { useEffect, useState, useRef } from "react";
import { api, apiError, money, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Package, Pencil, ImagePlus, Loader2 } from "lucide-react";

const CATS = ["Grocery", "FMCG", "Personal Care", "Home Care", "Stationery", "Cosmetics", "Household", "Other"];
const empty = { name: "", brand: "", category: "", sku: "", hsn: "", mrp: "", base_price: "", retailer_price: "",
  gst_rate: 18, moq: 1, units_per_case: 1, inventory: 0, dispatch_time: "", description: "", images: [], status: "ACTIVE" };

export default function Products() {
  const { profile } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const verified = profile?.verification_status === "VERIFIED";
  const load = () => api.get("/manufacturer/products").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };
  const margin = form.mrp && form.retailer_price ? (((form.mrp - form.retailer_price) / form.mrp) * 100).toFixed(1) : "0";

  async function upload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...(form.images || []), fileUrl(data.url)]);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form, mrp: +form.mrp, base_price: +form.base_price, retailer_price: +form.retailer_price,
        gst_rate: +form.gst_rate, moq: +form.moq, units_per_case: +form.units_per_case, inventory: +form.inventory };
      if (editId) await api.put(`/manufacturer/products/${editId}`, payload);
      else await api.post("/manufacturer/products", payload);
      toast.success(editId ? "Product updated" : "Product added");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }

  if (!items) return <Loader />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-forest">Products</h1>
        <Button data-testid="add-product-btn" className="bg-forest text-white hover:bg-forest/90" disabled={!verified} onClick={openNew}>
          <Plus className="mr-2 h-4 w-4" /> Add Product
        </Button>
      </div>
      {!verified && <p className="rounded-md bg-amber-50 px-4 py-2 text-sm text-amber-800">Get verified by VIPER OWL admin to start listing products.</p>}

      {items.length === 0 ? (
        <EmptyState title="No products yet" hint="Add your first product to start selling on VIPER OWL." icon={Package}
          action={verified && <Button className="bg-forest text-white hover:bg-forest/90" onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Add Product</Button>} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-psage/40 text-left"><tr className="overline text-forest">
              <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">MRP</th>
              <th className="px-4 py-3">Price</th><th className="px-4 py-3">Stock</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-t border-border hover:bg-psage/20">
                  <td className="px-4 py-3"><div className="font-medium text-ink">{p.name}</div><div className="text-xs text-ink/50">{p.brand}</div></td>
                  <td className="num px-4 py-3 text-ink/70">{p.sku}</td>
                  <td className="num px-4 py-3">{money(p.mrp)}</td>
                  <td className="num px-4 py-3 text-forest">{money(p.retailer_price)}</td>
                  <td className="num px-4 py-3">{p.inventory}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3"><Button data-testid={`edit-product-${p.id}`} variant="ghost" size="icon" onClick={() => openEdit(p)}><Pencil className="h-4 w-4 text-olive" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Product name" span><Input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></F>
            <F label="Brand"><Input data-testid="pf-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} /></F>
            <F label="Category"><Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="pf-category"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></F>
            <F label="SKU"><Input data-testid="pf-sku" value={form.sku} onChange={(e) => set("sku", e.target.value)} /></F>
            <F label="HSN/SAC"><Input data-testid="pf-hsn" value={form.hsn} onChange={(e) => set("hsn", e.target.value)} /></F>
            <F label="MRP (₹)"><Input data-testid="pf-mrp" type="number" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} /></F>
            <F label="Base price (₹)"><Input data-testid="pf-base" type="number" value={form.base_price} onChange={(e) => set("base_price", e.target.value)} /></F>
            <F label="Retailer price (₹)"><Input data-testid="pf-price" type="number" value={form.retailer_price} onChange={(e) => set("retailer_price", e.target.value)} /></F>
            <F label="GST %"><Input data-testid="pf-gst" type="number" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} /></F>
            <F label="MOQ"><Input data-testid="pf-moq" type="number" value={form.moq} onChange={(e) => set("moq", e.target.value)} /></F>
            <F label="Units / case"><Input data-testid="pf-upc" type="number" value={form.units_per_case} onChange={(e) => set("units_per_case", e.target.value)} /></F>
            <F label="Inventory"><Input data-testid="pf-inv" type="number" value={form.inventory} onChange={(e) => set("inventory", e.target.value)} /></F>
            <F label="Dispatch time"><Input data-testid="pf-dispatch" placeholder="2-4 days" value={form.dispatch_time} onChange={(e) => set("dispatch_time", e.target.value)} /></F>
            <F label="Description" span><Textarea data-testid="pf-desc" value={form.description} onChange={(e) => set("description", e.target.value)} /></F>
            <F label="Images" span>
              <div className="flex flex-wrap items-center gap-2">
                {(form.images || []).map((img, i) => <img key={i} src={img} alt="" className="h-14 w-14 rounded-md border border-border object-cover" />)}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
                <Button type="button" data-testid="pf-upload" variant="outline" className="border-dashed" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                </Button>
              </div>
            </F>
          </div>
          <p className="text-sm text-ink/60">Potential margin for retailer: <b className="num text-forest">{margin}%</b></p>
          <DialogFooter><Button data-testid="pf-save" className="bg-forest text-white hover:bg-forest/90" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save product"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children, span }) => (<div className={span ? "col-span-2" : ""}><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>);
